import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import "../../Styles/Modal.css";

// Generic modal shell built on Bootstrap's modal primitives - composed via props instead of each feature hand-rolling its own backdrop/escape-key handling.
export default function Modal({
    open,
    onClose,
    title,
    subtitle,
    icon,
    size = "md",
    scrollable = false,
    footer,
    children,
    closeOnBackdrop = true,
    ariaLabel,
}) {
    useEffect(() => {
        if (!open) return;

        const handleEscape = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [open, onClose]);

    if (!open) return null;

    const sizeClass = size ? `modal-${size}` : "";

    // Portalled to <body> so the backdrop covers the viewport even inside transformed/animated page containers.
    return createPortal(
        <div
            className="modal d-block hc-modal-backdrop"
            tabIndex="-1"
            role="presentation"
            onClick={(e) => {
                if (closeOnBackdrop && e.target === e.currentTarget) onClose();
            }}
        >
            <div className={`modal-dialog modal-dialog-centered ${sizeClass} ${scrollable ? "modal-dialog-scrollable" : ""}`}>
                <div
                    className="modal-content hc-modal-content border-0"
                    role="dialog"
                    aria-modal="true"
                    aria-label={ariaLabel || title}
                >
                    {(title || icon || onClose) && (
                        <div className="hc-modal-header">
                            <div className="d-flex align-items-start gap-3" style={{ minWidth: 0 }}>
                                {icon && <span className="hc-modal-icon">{icon}</span>}
                                <div className="hc-modal-header-text">
                                    {title && <h5 className="modal-title fw-bold mb-1">{title}</h5>}
                                    {subtitle && <small className="text-muted d-block">{subtitle}</small>}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="hc-modal-close"
                                onClick={onClose}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>
                    )}
                    <div className="hc-modal-body">
                        {children}
                    </div>
                    {footer && (
                        <div className="hc-modal-footer">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
